import React, { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useMasterData, useLocationCascade } from '../../hooks/useMasterData';
import { complaintService } from '../../services/complaint/complaintService';
import { CreateComplaintDto } from '../../types/complaint';
import { useAuth } from '../../context/AuthContext';
import { Upload, AlertCircle } from 'lucide-react';

interface RaiseComplaintModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const RaiseComplaintModal: React.FC<RaiseComplaintModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const { plants, departments, categories, priorities } = useMasterData();
  const isDepartmentScoped = user?.role === 'OFFICER' || user?.role === 'DEPARTMENT_USER';
  const lockedDepartmentId = isDepartmentScoped && user?.department_id ? user.department_id : '';
  const lockedPlantId = isDepartmentScoped && user?.plant_id ? user.plant_id : '';
  const [selectedPlant, setSelectedPlant] = useState(lockedPlantId);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedFloor, setSelectedFloor] = useState('');
  const { buildings, floors, locations } = useLocationCascade(selectedPlant, selectedBuilding, selectedFloor);

  const [formData, setFormData] = useState<Partial<CreateComplaintDto>>({
    title: '',
    description: '',
    department_id: lockedDepartmentId,
    category_id: '',
    priority_name: 'Medium',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.department_id || !formData.category_id || !selectedPlant) {
      setErrorMsg('Please complete all required fields.');
      return;
    }

    setIsSubmitting(true);
    setErrorMsg('');

    try {
      const res = await complaintService.createComplaint({
        title: formData.title!,
        description: formData.description!,
        department_id: formData.department_id!,
        plant_id: selectedPlant,
        building_id: selectedBuilding,
        location_id: formData.location_id || 'LOC-001',
        category_id: formData.category_id!,
        priority_name: formData.priority_name as any,
      });

      if (res.success) {
        onSuccess();
        onClose();
      } else {
        setErrorMsg(res.message || 'Failed to submit complaint');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'An error occurred');
    }
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Raise New Facility Complaint">
      <form onSubmit={handleSubmit} className="space-y-4">
        {errorMsg && (
          <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs rounded-lg flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-slate-300 mb-1">Complaint Title *</label>
          <input
            type="text"
            required
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g. AC cooling failure in Server Room"
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">Reporting Department *</label>
            <select
              required
              disabled={!!lockedDepartmentId}
              value={formData.department_id}
              onChange={e => setFormData({ ...formData, department_id: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
            >
              <option value="">Select Department</option>
              {lockedDepartmentId && !departments.some(d => d.department_id === lockedDepartmentId) && (
                <option value={lockedDepartmentId}>{lockedDepartmentId}</option>
              )}
              {departments.map(d => (
                <option key={d.department_id} value={d.department_id}>{d.name}</option>
              ))}
            </select>
            {lockedDepartmentId && (
              <p className="text-[10px] text-slate-500 mt-1">Your department (locked)</p>
            )}
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1">Work Category *</label>
            <select
              required
              value={formData.category_id}
              onChange={e => setFormData({ ...formData, category_id: e.target.value })}
              className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
            >
              <option value="">Select Category</option>
              {categories.map(c => (
                <option key={c.category_id} value={c.category_id}>{c.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Location Cascade */}
        <div className="space-y-2 border-t border-slate-800 pt-3">
          <p className="text-xs font-semibold text-sky-400">Location Taxonomy (Dynamic Cascade)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div>
              <label className="block text-[10px] text-slate-400">Plant *</label>
              <select
                required
                disabled={!!lockedPlantId}
                value={selectedPlant}
                onChange={e => { setSelectedPlant(e.target.value); setSelectedBuilding(''); setSelectedFloor(''); }}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-1.5 text-xs text-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed"
              >
                <option value="">Select Plant</option>
                {plants.map(p => <option key={p.plant_id} value={p.plant_id}>{p.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[10px] text-slate-400">Building</label>
              <select
                value={selectedBuilding}
                onChange={e => { setSelectedBuilding(e.target.value); setSelectedFloor(''); }}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-1.5 text-xs text-slate-100"
              >
                <option value="">Select Building</option>
                {buildings.map(b => <option key={b.building_id} value={b.building_id}>{b.name}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-[10px] text-slate-400">Specific Location</label>
              <select
                value={formData.location_id || ''}
                onChange={e => setFormData({ ...formData, location_id: e.target.value })}
                className="w-full bg-slate-950 border border-slate-700 rounded-lg p-1.5 text-xs text-slate-100"
              >
                <option value="">Select Location</option>
                {locations.map(l => <option key={l.location_id} value={l.location_id}>{l.name}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-300 mb-1">Severity Priority *</label>
          <select
            value={formData.priority_name}
            onChange={e => setFormData({ ...formData, priority_name: e.target.value as any })}
            className="w-full bg-slate-950 border border-slate-700 rounded-lg px-3 py-2 text-xs text-slate-100"
          >
            {priorities.map(p => (
              <option key={p.priority_id} value={p.name}>{p.name} ({p.sla_hours} hrs SLA)</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-300 mb-1">Issue Description *</label>
          <textarea
            required
            rows={3}
            value={formData.description}
            onChange={e => setFormData({ ...formData, description: e.target.value })}
            placeholder="Detailed description of the issue or fault..."
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-sky-500"
          />
        </div>

        {/* Photo Upload Dropzone */}
        <div className="border-2 border-dashed border-slate-800 rounded-xl p-4 text-center hover:border-slate-700 transition-colors">
          <Upload className="w-6 h-6 text-slate-500 mx-auto mb-1" />
          <p className="text-xs text-slate-300 font-medium">Attach Photos or Support Documents</p>
          <p className="text-[10px] text-slate-500 mt-0.5">PNG, JPG, PDF up to 10MB</p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="primary" isLoading={isSubmitting}>Submit Complaint</Button>
        </div>
      </form>
    </Modal>
  );
};
